/**
 * Validates the shares consolidation or subdivision form on submit.
 *
 * Rules:
 * - newShares: required, positive whole number
 * - newParValue: required, positive decimal allowed
 * - newShares × newParValue must equal nominalTotal
 *
 * Consolidation:
 * - newShares < currentShares
 * - newParValue > currentParValue
 *
 * Subdivision:
 * - newShares > currentShares
 * - newParValue < currentParValue
 */
export function validateConsolidationAndSubdivision({
  changeType,
  currentShares,
  currentParValue,
  nominalTotal,
  newShares,
  newParValue,
}) {
  const errs = {};
  const isConsolidation = changeType === "sharesConsolidation";
  const ns = Number(newShares);
  const npv = Number(newParValue);

  if (newShares === "" || newShares == null || isNaN(ns)) {
    errs.newShares = "Number of shares is required.";
  } else if (ns <= 0) {
    errs.newShares = "Number of shares must be greater than zero.";
  } else if (!Number.isInteger(ns)) {
    errs.newShares = "Number of shares must be a whole number.";
  } else if (isConsolidation && ns >= currentShares) {
    errs.newShares = `Must be less than the current ${currentShares.toLocaleString()} shares.`;
  } else if (!isConsolidation && ns <= currentShares) {
    errs.newShares = `Must be greater than the current ${currentShares.toLocaleString()} shares.`;
  }

  if (newParValue === "" || newParValue == null || isNaN(npv)) {
    errs.newParValue = "Par value per share is required.";
  } else if (npv <= 0) {
    errs.newParValue = "Par value must be greater than zero.";
  } else if (isConsolidation && npv <= currentParValue) {
    errs.newParValue = `Must be higher than the current KES ${currentParValue.toLocaleString()}.`;
  } else if (!isConsolidation && npv >= currentParValue) {
    errs.newParValue = `Must be lower than the current KES ${currentParValue.toLocaleString()}.`;
  }

  if (!errs.newShares && !errs.newParValue && nominalTotal > 0) {
    const computed = ns * npv;
    if (Math.abs(computed - nominalTotal) > 0.000001) {
      errs.newParValue = `The entered values must multiply to KES ${nominalTotal.toLocaleString()}. Currently they equal KES ${computed.toLocaleString()}.`;
    }
  }

  return errs;
}

/**
 * Given the edited field value, derive the counterpart so:
 * shares × parValue = nominalTotal
 */
export function deriveCounterpart(changedField, changedValue, nominalTotal) {
  if (!changedValue || changedValue <= 0 || !nominalTotal || nominalTotal <= 0) {
    return null;
  }
  return nominalTotal / changedValue;
}

export function isWholeNumber(n) {
  const num = Number(n);
  return !isNaN(num) && num > 0 && Number.isInteger(num);
}